import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';

export interface NicheTemplateItem {
  id: string;
  name: string;
  category: string;
  unit: string;
  estimated_price: number;
  niche: string;
  keywords: string[];
}

export interface ExistingMaterial {
  id?: string;
  name: string;
  category?: string;
  unit?: string;
  price?: number;
}

interface DbTemplateItem {
  id: string;
  nicho: string;
  categoria: string;
  nome_item: string;
  unidade: string;
  created_at?: string;
}

export interface UseNicheTemplatesProps {
  userNiche: string;
  existingMaterials: ExistingMaterial[];
  userId: string;
}

function normalizeString(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function getDismissKey(userId: string, niche: string): string {
  return `dismissed_suggestions_${userId}_${normalizeString(niche)}`;
}

export function useNicheTemplates({ userNiche, existingMaterials, userId }: UseNicheTemplatesProps) {
  const [allTemplates, setAllTemplates] = useState<NicheTemplateItem[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => {
    try {
      const key = getDismissKey(userId, userNiche);
      const raw = localStorage.getItem(key);
      return new Set<string>(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set<string>();
    }
  });

  const dismissSuggestion = useCallback((id: string) => {
    setDismissedIds(prev => {
      const next = new Set(prev).add(id);
      try {
        const key = getDismissKey(userId, userNiche);
        localStorage.setItem(key, JSON.stringify([...next]));
      } catch {
        // localStorage indisponível — ignora silenciosamente
      }
      return next;
    });
  }, [userId, userNiche]);

  const fetchTemplates = useCallback(async (isCancelled?: () => boolean): Promise<void> => {
    setIsLoadingSuggestions(true);
    setErrorMessage(null);
    try {
      const { data, error } = await supabase
        .from('niche_templates')
        .select('*');

      if (isCancelled && isCancelled()) return;

      if (error) {
        throw new Error(error.message);
      }

      if (data) {
        const mapped: NicheTemplateItem[] = (data as DbTemplateItem[]).map((item) => {
          // Extrair palavras-chave significativas para busca inteligente
          const cleanName = normalizeString(item.nome_item);
          const cleanNiche = normalizeString(item.nicho);
          const words = `${cleanName} ${cleanNiche}`
            .split(/\s+/)
            .filter((w) => w.length > 2);

          return {
            id: item.id,
            name: item.nome_item,
            category: item.categoria,
            unit: item.unidade,
            estimated_price: 0,
            niche: item.nicho,
            keywords: Array.from(new Set(words)),
          };
        });

        setAllTemplates(mapped);
      }
    } catch (err: unknown) {
      if (isCancelled && isCancelled()) return;
      const msg = err instanceof Error ? err.message : 'Erro desconhecido ao carregar templates';
      setErrorMessage(msg);
      console.error('useNicheTemplates error:', err);
    } finally {
      if (isCancelled && isCancelled()) return;
      setIsLoadingSuggestions(false);
    }
  }, []);

  // Busca inicial
  useEffect(() => {
    let ignore = false;
    fetchTemplates(() => ignore);
    return () => {
      ignore = true;
    };
  }, [fetchTemplates]);

  // Filtragem inteligente reativa derivada com useMemo
  const suggestions = useMemo<NicheTemplateItem[]>(() => {
    if (allTemplates.length === 0) {
      return [];
    }

    const cleanUserNiche = normalizeString(userNiche);
    
    // Passo 1: Filtrar por nicho correspondente exato
    let filtered = allTemplates.filter(
      (t) => normalizeString(t.niche) === cleanUserNiche
    );

    // Passo 2 (Fallback): Removido para evitar correspondências absurdas e imprecisas
    /*
    if (filtered.length < 3 && cleanUserNiche.length > 0) {
      const nicheWords = cleanUserNiche.split(/\s+/).filter((w) => w.length > 2);
      
      if (nicheWords.length > 0) {
        const fallbackMatches = allTemplates.filter((t) => {
          // Evita duplicar itens já encontrados no Passo 1
          if (filtered.some((item) => item.id === t.id)) return false;
          
          // Verifica se alguma palavra-chave do nicho do usuário está nas keywords do template
          return nicheWords.some((nicheWord) =>
            t.keywords.some((kw) => kw.includes(nicheWord) || nicheWord.includes(kw))
          );
        });

        filtered = [...filtered, ...fallbackMatches];
      }
    }
    */

    // Passo 3 (Exclusão): Remove templates que já constam nos materiais existentes
    const existingNamesSet = new Set(
      existingMaterials.map((m) => normalizeString(m.name))
    );

    // Passo 4 (Exclusão): Remove itens descartados pelo usuário
    return filtered.filter(
      (t) => !existingNamesSet.has(normalizeString(t.name)) && !dismissedIds.has(t.id)
    );
  }, [allTemplates, userNiche, existingMaterials, dismissedIds]);

  return {
    suggestions,
    allTemplates,
    isLoadingSuggestions,
    errorMessage,
    dismissSuggestion,
    refetchTemplates: fetchTemplates,
  };
}
